const STORAGE_KEY = 'tff_leads';

// ── Sync helper: fire-and-forget push to Google Sheets API ──────────────────
const syncToSheets = (lead) => {
  fetch('/api/leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(lead),
  }).catch(() => {}); // non-blocking — localStorage is the fallback
};

const syncUpdateToSheets = (id, updates) => {
  fetch(`/api/leads?id=${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  }).catch(() => {});
};

// ── Generate unique client reference code ────────────────────────────────────
export const generateRefCode = () => {
  const date = new Date();
  const d = `${date.getFullYear()}${String(date.getMonth()+1).padStart(2,'0')}${String(date.getDate()).padStart(2,'0')}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `TFF-${d}-${rand}`;
};

// ── CRUD ─────────────────────────────────────────────────────────────────────
export const getLeads = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

/**
 * Fetch all leads from Google Sheets (source of truth).
 * Falls back to localStorage if the API is unreachable.
 */
export const fetchLeadsFromSheets = async (jwtToken) => {
  try {
    const res = await fetch('/api/leads', {
      headers: { 'Authorization': `Bearer ${jwtToken}` },
    });
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    const leads = data.leads || [];
    // Update local cache
    localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));
    return leads;
  } catch {
    // Fallback to localStorage
    return getLeads();
  }
};

export const saveLead = (lead) => {
  const leads = getLeads();
  const refCode = generateRefCode();
  const newLead = {
    id: Date.now().toString(),
    refCode,                            // e.g. TFF-20260402-0001
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'New',
    notes: '',
    followUpDate: null,
    emailSent: false,
    paymentStatus: 'unpaid',            // unpaid | paid
    packagePaid: null,
    amountPaid: 0,
    source: 'website',
    ...lead,
  };
  leads.unshift(newLead);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));

  // Sync to Google Sheets
  syncToSheets(newLead);

  return newLead;
};

export const updateLead = (id, updates) => {
  const leads = getLeads();
  const idx = leads.findIndex(l => l.id === id);
  if (idx === -1) return null;
  leads[idx] = { ...leads[idx], ...updates, updatedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(leads));

  // Sync to Google Sheets
  syncUpdateToSheets(id, updates);

  return leads[idx];
};

export const deleteLead = (id) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(getLeads().filter(l => l.id !== id)));
  // Mark as deleted in Sheets (soft delete)
  syncUpdateToSheets(id, { status: 'Deleted', updatedAt: new Date().toISOString() });
};

// ── Reactivation queue ────────────────────────────────────────────────────────
export const getReactivationQueue = () => {
  const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  return getLeads().filter(
    l => ['Inactive','New','Contacted'].includes(l.status) && l.updatedAt < cutoff
  );
};

// ── Stats ─────────────────────────────────────────────────────────────────────
export const getStats = (packageValues) => {
  const leads = getLeads();
  const byStatus = {}, byPackage = {};
  let totalRevenue = 0, totalPaid = 0;

  leads.forEach(l => {
    byStatus[l.status] = (byStatus[l.status] || 0) + 1;
    byPackage[l.packageInterest] = (byPackage[l.packageInterest] || 0) + 1;
    if (l.paymentStatus === 'paid' && l.amountPaid) totalPaid += l.amountPaid;
    if (l.status === 'Active Member' && packageValues[l.packageInterest])
      totalRevenue += packageValues[l.packageInterest];
  });

  return {
    total: leads.length,
    byStatus,
    byPackage,
    totalRevenue,
    totalPaid,                                          // actual cash collected
    siteOwnerRevenue: Math.round(totalRevenue * 0.33),
    gymOwnerRevenue:  Math.round(totalRevenue * 0.67),
    siteOwnerPaid:    Math.round(totalPaid * 0.33),
    gymOwnerPaid:     Math.round(totalPaid * 0.67),
    conversionRate: leads.length > 0
      ? Math.round(((byStatus['Active Member'] || 0) / leads.length) * 100) : 0,
    paidCount: leads.filter(l => l.paymentStatus === 'paid').length,
  };
};

// ── Abandoned checkout tracking ──────────────────────────────────────────────
export const markAbandoned = (id) => {
  const lead = updateLead(id, { status: 'Abandoned', abandonedAt: new Date().toISOString() });
  if (lead && lead.email) {
    // Fire webhook to trigger abandoned checkout email
    fetch('/api/abandoned-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientName: lead.name,
        clientEmail: lead.email,
        clientPhone: lead.phone,
        refCode: lead.refCode,
        packageInterest: lead.packageInterest || null,
        abandonedAt: lead.abandonedAt,
      }),
    }).catch(() => {}); // non-blocking
  }
  return lead;
};

// ── Get leads that need follow-up (abandoned > 30 min ago, no email sent) ────
export const getAbandonedLeads = () => {
  const cutoff = new Date(Date.now() - 30 * 60 * 1000).toISOString(); // 30 min
  return getLeads().filter(
    l => l.status === 'Abandoned' && !l.abandonedEmailSent && l.abandonedAt && l.abandonedAt < cutoff
  );
};

// ── CSV export ────────────────────────────────────────────────────────────────
export const exportToCSV = () => {
  const leads = getLeads();
  if (!leads.length) return;
  const headers = ['Ref Code','Name','Phone','Email','Package','Status','Payment','Amount Paid','Source','Notes','Follow-Up','Created'];
  const rows = leads.map(l => [
    l.refCode || '',
    l.name || '',
    l.phone || '',
    l.email || '',
    l.packageInterest || '',
    l.status || '',
    l.paymentStatus || '',
    l.amountPaid || 0,
    l.source || '',
    (l.notes || '').replace(/,/g,';'),
    l.followUpDate || '',
    new Date(l.createdAt).toLocaleDateString(),
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `tff-leads-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
};
